import { useQuery } from 'react-query'

const DASHBOARD_BASE_URL =
  import.meta.env.VITE_GO_API_URL || 'http://localhost:8081'

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function dashFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${DASHBOARD_BASE_URL}${path}`, {
    headers: authHeaders(),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`Dashboard API ${res.status}: ${text}`)
  }
  return res.json()
}

// ---------------------------------------------------------------------------
// Types (mirror backend-go gen/dashboard structs, JSON-encoded with snake_case)
// ---------------------------------------------------------------------------

export interface ClientCount {
  client_id: string
  client_name: string
  count: number
}

export interface InvoiceQueues {
  pending_review_total: number
  pending_review_top: ClientCount[]
  ready_to_export_total: number
  ready_to_export_top: ClientCount[]
  failed_total: number
  failed_top: ClientCount[]
}

export interface UnmatchedStatement {
  statement_id: string
  statement_name: string
  iban?: string | null
  account_id?: number | null
  count: number
}

export interface UnmatchedClientGroup {
  client_id: string
  client_name: string
  count: number
  statements: UnmatchedStatement[]
}

export interface ReconciliationQueue {
  unmatched_total: number
  unmatched_top: UnmatchedClientGroup[]
}

export interface ThroughputPoint {
  date: string
  completed: number
  review_required: number
  failed: number
  reviewed: number
}

export interface TopCorrectedField {
  field: string
  count: number
}

export interface AIAccuracy {
  auto_approved_pct: number
  avg_confidence: number
  top_corrected_fields: TopCorrectedField[]
  sample_size: number
}

export interface ReconciliationStatsPoint {
  date: string
  matched: number
  total: number
  match_rate_pct: number
}

export interface ClientSummaryRow {
  client_id: string
  client_name: string
  invoices_in_range: number
  pending_review: number
  ready_to_export: number
  last_activity?: string
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const REFRESH_MS = 60_000

export function useInvoiceQueues() {
  return useQuery<InvoiceQueues>(
    ['dashboard', 'invoice-queues'],
    () => dashFetch<InvoiceQueues>('/api/v1/dashboard/invoice-queues'),
    { refetchInterval: REFRESH_MS }
  )
}

export function useReconciliationQueue() {
  return useQuery<ReconciliationQueue>(
    ['dashboard', 'reconciliation-queue'],
    () => dashFetch<ReconciliationQueue>('/api/v1/dashboard/reconciliation-queue'),
    { refetchInterval: REFRESH_MS }
  )
}

export function useThroughput(days: number) {
  return useQuery<ThroughputPoint[]>(
    ['dashboard', 'throughput', days],
    () => dashFetch<ThroughputPoint[]>(`/api/v1/dashboard/throughput?days=${days}`)
  )
}

export function useAIAccuracy(days: number) {
  return useQuery<AIAccuracy>(
    ['dashboard', 'ai-accuracy', days],
    () => dashFetch<AIAccuracy>(`/api/v1/dashboard/ai-accuracy?days=${days}`)
  )
}

export function useReconciliationStats(days: number) {
  return useQuery<ReconciliationStatsPoint[]>(
    ['dashboard', 'reconciliation-stats', days],
    () =>
      dashFetch<ReconciliationStatsPoint[]>(
        `/api/v1/dashboard/reconciliation-stats?days=${days}`
      )
  )
}

export function useClientsSummary(days: number) {
  return useQuery<ClientSummaryRow[]>(
    ['dashboard', 'clients-summary', days],
    () => dashFetch<ClientSummaryRow[]>(`/api/v1/dashboard/clients-summary?days=${days}`)
  )
}
